import { rem } from 'polished'
import { InterpolationValue } from 'styled-components'

import { media } from './media'
import { css } from './themedStyledComponents'

export enum FontFamily {
  base = 'Futura, Arial',
}

export type TextFontWeight = 'normal' | 'bold'

export enum FontWeight {
  // light = 300,
  regular = 400,
  // medium = 500,
  // semiBold = 600,
  bold = 700,
}

export interface TypiValue {
  size: number
  lineHeight: number
  wordSpacing?: string
  family?: FontFamily
  weight?: FontWeight
}

export interface TypiSize {
  default: TypiValue
  tablet?: TypiValue
  desktop?: TypiValue
}

interface TypiConfig {
  [size: string]: TypiSize
}

export const typi: TypiConfig = {
  base: {
    default: {
      size: 16,
      lineHeight: 24,
    },
    desktop: {
      size: 16,
      lineHeight: 24,
    },
  },
  xxxs: {
    default: {
      size: 12,
      lineHeight: 20,
    },
  },
  xxs: {
    default: {
      size: 12,
      lineHeight: 20,
    },
    tablet: {
      size: 14,
      lineHeight: 20,
    },
  },
  xxsBold: {
    default: {
      size: 14,
      lineHeight: 20,
      wordSpacing: '-.05em',
      weight: FontWeight.bold,
    },
  },
  xs: {
    default: {
      size: 16,
      lineHeight: 25,
    },
  },
  xsBold: {
    default: {
      size: 16,
      lineHeight: 25,
      wordSpacing: '-.05em',
      weight: FontWeight.bold,
    },
  },
  s: {
    default: {
      size: 16,
      lineHeight: 25,
    },
    tablet: {
      size: 20,
      lineHeight: 30,
    },
  },
  sBold: {
    default: {
      size: 16,
      lineHeight: 25,
      wordSpacing: '-.05em',
      weight: FontWeight.bold,
    },
    tablet: {
      size: 20,
      lineHeight: 30,
      wordSpacing: '-.05em',
      weight: FontWeight.bold,
    },
  },
  m: {
    default: {
      size: 24,
      lineHeight: 35,
    },
    tablet: {
      size: 24,
      lineHeight: 35,
      wordSpacing: '-.05em',
    },
  },
  mBold: {
    default: {
      size: 24,
      lineHeight: 35,
      wordSpacing: '-.05em',
      weight: FontWeight.bold,
    },
    tablet: {
      size: 28,
      lineHeight: 35,
      wordSpacing: '-.05em',
      weight: FontWeight.bold,
    },
  },
  l: {
    default: {
      size: 25,
      lineHeight: 35,
    },
    tablet: {
      size: 34,
      lineHeight: 45,
    },
  },
  lBold: {
    default: {
      size: 25,
      lineHeight: 35,
      wordSpacing: '-.05em',
      weight: FontWeight.bold,
    },
    tablet: {
      size: 34,
      lineHeight: 45,
      wordSpacing: '-.05em',
      weight: FontWeight.bold,
    },
  },
  xl: {
    default: {
      size: 30,
      lineHeight: 40,
    },
    tablet: {
      size: 40,
      lineHeight: 55,
    },
  },
  xlBold: {
    default: {
      size: 30,
      lineHeight: 40,
      wordSpacing: '-.05em',
      weight: FontWeight.bold,
    },
    tablet: {
      size: 40,
      lineHeight: 55,
      wordSpacing: '-.05em',
      weight: FontWeight.bold,
    },
  },
  xxl: {
    default: {
      size: 45,
      lineHeight: 52.5,
    },
    tablet: {
      size: 60,
      lineHeight: 70,
    },
  },
  xxlBold: {
    default: {
      size: 38,
      lineHeight: 55,
      wordSpacing: '-.05em',
      weight: FontWeight.bold,
    },
    tablet: {
      size: 60,
      lineHeight: 70,
      wordSpacing: '-.05em',
      weight: FontWeight.bold,
    },
  },
}

type FontName = (
  fontWeight?: FontWeight | TextFontWeight,
) => InterpolationValue[]

export interface FontInterface {
  base: FontName
  xxs: FontName
  xs: FontName
  s: FontName
  m: FontName
  mBold: FontName
  l: FontName
  xl: FontName
  xxl: FontName
}

export const font = {
  base: generateResponsiveFontSize(typi.base),

  xxxs: generateResponsiveFontSize(typi.xxxs),
  xxs: generateResponsiveFontSize(typi.xxs),
  xs: generateResponsiveFontSize(typi.xs),
  s: generateResponsiveFontSize(typi.s),
  m: generateResponsiveFontSize(typi.m),
  l: generateResponsiveFontSize(typi.l),
  xl: generateResponsiveFontSize(typi.xl),
  xxl: generateResponsiveFontSize(typi.xxl),

  xxsBold: generateResponsiveFontSize(typi.xxsBold),
  xsBold: generateResponsiveFontSize(typi.xsBold),
  sBold: generateResponsiveFontSize(typi.sBold),
  mBold: generateResponsiveFontSize(typi.mBold),
  lBold: generateResponsiveFontSize(typi.lBold),
  xlBold: generateResponsiveFontSize(typi.xlBold),
  xxlBold: generateResponsiveFontSize(typi.xxlBold),
}

function generateResponsiveFontSize(size: TypiSize) {
  const fontBaseCss = css`
    ${generateFontSize(size.default)};

    ${size.tablet &&
      media.tablet`
      ${generateFontSize(size.tablet)}
    `};

    ${size.desktop &&
      media.desktop`
      ${generateFontSize(size.desktop)}
    `};
  `
  return (fontWeight?: FontWeight | TextFontWeight) => css`
    ${fontWeight &&
      css`
        font-weight: ${FontWeight[fontWeight]};
      `}
    ${fontBaseCss};
  `
}

function generateFontSize(value: TypiValue) {
  return css`
    font-family: ${value.family ? value.family : FontFamily.base};
    font-size: ${rem(value.size)};
    line-height: ${value.lineHeight / value.size};
    font-weight: ${FontWeight[value.weight || FontWeight.regular]};
    word-spacing: ${value.wordSpacing};
  `
}
